import { createSlice } from "@reduxjs/toolkit";

import {
  createCompanyThunk,
  deleteCompanyThunk,
  getCompanyThunk,
  updateCompanyThunk,
} from "state/action/company";

// Initial state
const initialState = {
  // Get Company inisial state
  getCompany: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Create Company inisial state
  createCompany: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Update Company inisial state
  updateCompany: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete Company inisial state
  deleteCompany: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  createCompanyInputBody: {
    name: "",
    slug: "",
    subdomain: "",
    status: "trial",
    isActive: true,
    owner: null, // User ID (Mocked)

    // Plan & Limits
    plan: "free",
    trialEndsAt: null,
    subscriptionEndsAt: null,
    limits: {
      maxUsers: 5,
      maxWorkUnits: 10,
      maxTenants: 50,
    },

    // Settings
    settings: {
      timezone: "UTC",
      currency: "USD",
      dateFormat: "YYYY-MM-DD",
      enableFieldLevelSecurity: true,
      enableAuditLog: true,
      allowCustomRoles: true,
    },

    // Contact (Nested Address)
    contact: {
      email: "",
      phone: "",
      address: {
        street: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
      },
    },
  },
  // Company object value
  company_object_value: "",
};

export const companySlice = createSlice({
  name: "Company",
  initialState,
  reducers: {
    // 1. Root Level Fields
    setField: (state, action) => {
      const { field, value } = action.payload;
      state.createCompanyInputBody[field] = value;
    },
    // 2. First Level Nested (settings, limits, contact-direct)
    setNestedField: (state, action) => {
      const { parent, field, value } = action.payload;
      state.createCompanyInputBody[parent][field] = value;
    },
    // 3. Deep Nested (contact.address)
    setAddressField: (state, action) => {
      const { field, value } = action.payload;
      state.createCompanyInputBody.contact.address[field] = value;
    },
    resetForm: (state) => {
      state.createCompanyInputBody = initialState.createCompanyInputBody;
    },
    // Update field level 1 (name, code, logo, isActive)
    setCompanyField: (state, action) => {
      const { field, value } = action.payload;
      state.createCompanyInputBody[field] = value;
    },

    resetCompanyForm: (state) => {
      state.createCompanyInputBody = initialState.createCompanyInputBody;
    },
    setGetCompanyObjectValue: (state, action) => {
      return {
        ...state,
        company_object_value: action.payload,
      };
    },
    // note: clear state
    setClearGetCompanyObjectValue: (state, _) => {
      return {
        ...state,
        company_object_value: "",
      };
    },
    setClearGetCompany: (state, _) => {
      return {
        ...state,
        getCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearCreateCompany: (state, _) => {
      return {
        ...state,
        createCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUpdateCompany: (state, _) => {
      return {
        ...state,
        updateCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeleteCompany: (state, _) => {
      return {
        ...state,
        deleteCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllCompanyState: (state, _) => {
      return {
        ...state,
        // Get Company inisial state
        getCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Create Company inisial state
        createCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update Company inisial state
        updateCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete Company inisial state
        deleteCompany: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Company object value
        company_object_value: "",
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Company reducer
      .addCase(getCompanyThunk.pending, (state, _) => {
        return {
          ...state,
          getCompany: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getCompanyThunk.fulfilled, (state, action) => {
        return {
          ...state,
          getCompany: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getCompanyThunk.rejected, (state, action) => {
        return {
          ...state,
          getCompany: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Create Company reducer
      .addCase(createCompanyThunk.pending, (state, _) => {
        return {
          ...state,
          createCompany: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createCompanyThunk.fulfilled, (state, action) => {
        return {
          ...state,
          createCompany: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createCompanyThunk.rejected, (state, action) => {
        return {
          ...state,
          createCompany: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update Company reducer
      .addCase(updateCompanyThunk.pending, (state, _) => {
        return {
          ...state,
          updateCompany: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateCompanyThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateCompany: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateCompanyThunk.rejected, (state, action) => {
        return {
          ...state,
          updateCompany: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const companyAction = {
  ...companySlice.actions,
  getCompanyThunk,
  createCompanyThunk,
  updateCompanyThunk,
  deleteCompanyThunk,
};

export function selectCompanyObjectValue(state) {
  return state?.company?.company_object_value;
}

export function selectCompany(state) {
  return state?.company;
}

export default companySlice.reducer;
